import axios from "axios";
import { notification } from "antd";
import { AUTH_TOKEN, USER_ID, AUTH_USER } from "constants/AuthConstant";

// Configurar interceptor global para limpar cache em caso de troca de usuário
let currentUserId = localStorage.getItem(USER_ID);

// Adicionar uma variável para controlar quando o token expirou
let tokenExpired = false;
let isLogoutInProgress = false;

// Config
const ENTRY_ROUTE = "/auth/login";
const TOKEN_PAYLOAD_KEY = "Authorization";
const PUBLIC_REQUEST_KEY = "public-request";

// Função para limpar dados de autenticação
const clearAuthData = (limparCacheBrowser = false) => {
  // Limpar LocalStorage
  localStorage.removeItem(AUTH_TOKEN);
  localStorage.removeItem(USER_ID);
  localStorage.removeItem(AUTH_USER);

  // Limpar SessionStorage
  try {
    // Limpar apenas dados específicos do dashboard, não todo o sessionStorage
    sessionStorage.removeItem("dashboard_indicators_data");
    sessionStorage.removeItem("dashboard_store_indicators");
    sessionStorage.removeItem("dashboard_config_cache");
  } catch (e) {
    console.error("[FetchInterceptor] Erro ao limpar sessionStorage:", e);
  }

  // Limpar apenas cookies de autenticação
  document.cookie.split(";").forEach(function (c) {
    if (
      c.includes("AUTH_TOKEN") ||
      c.includes("USER_ID") ||
      c.includes("AUTH_USER")
    ) {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    }
  });
};

// Função para redirecionar para login
const redirectToLogin = () => {
  if (isLogoutInProgress) return;
  isLogoutInProgress = true;

  //console.log("[FetchInterceptor] Iniciando processo de logout...");

  window.dispatchEvent(new CustomEvent("auth-failed"));

  const clearAuthData = () => {
    localStorage.removeItem(AUTH_TOKEN);
    localStorage.removeItem(USER_ID);
    localStorage.removeItem(AUTH_USER);
    sessionStorage.clear();
  };
  clearAuthData();

  const { logout } = require("store/auth.store").useAuth.getState();
  logout();

  const forceReload = () => {
    //console.log("[FetchInterceptor] Forçando recarregamento limpo...");
    window.location.replace(`${ENTRY_ROUTE}?forceReload=${Date.now()}`);
    window.location.reload(true);
  };

  setTimeout(() => {
    isLogoutInProgress = false;
    tokenExpired = false;
    forceReload();
  }, 100);
};

// Tentar limpar cache no carregamento inicial
(function limparCacheInicial() {
  const token = localStorage.getItem(AUTH_TOKEN);
  const pathname = window.location.pathname;

  // Se estamos na página de login sem token, limpar tudo
  if ((pathname.includes("/login") || pathname.includes("/auth")) && !token) {
    //console.log("[FetchInterceptor] Limpeza inicial de cache");
    clearAuthData(true);
  }
})();

// Criar uma instância do axios com configurações adequadas
const service = axios.create({
  // Não definir baseURL para permitir URLs absolutas
  timeout: 60000,
  // Removido os cabeçalhos anti-cache que estavam causando problemas de CORS
});

// Função para verificar se é uma rota de autenticação
const isAuthRoute = (url) => {
  const authRoutes = [
    "/Authentication",
    "/login",
    "/Token",
    "/auth/login",
    "/auth/Token",
    "/auth/Authentication",
  ];

  return authRoutes.some((route) => url.includes(route));
};

// Verificar se houve troca de usuário
const checkUserChange = () => {
  const storedUserId = localStorage.getItem(USER_ID);

  if (storedUserId !== currentUserId) {
    // Atualizar o ID de usuário atual
    currentUserId = storedUserId;

    // Limpar caches que podem conter dados do usuário anterior
    try {
      // Limpar flags de controle
      window._dashboardFetchedOnce = false;
      window._dashboardLastFetchedUser = null;

      // Notificar a aplicação sobre a troca de usuário
      window.dispatchEvent(
        new CustomEvent("user-changed", {
          detail: { userId: storedUserId },
        })
      );
    } catch (e) {
      console.error(
        "[FetchInterceptor] Erro ao limpar cache após troca de usuário:",
        e
      );
    }

    return true;
  }

  return false;
};

// Request interceptor
service.interceptors.request.use(
  (config) => {
    //console.log("[FetchInterceptor] Requisição interceptada:", config.url);
    // Verificar se houve troca de usuário
    const userChanged = checkUserChange();

    // Verificar Token no localStorage
    const jwtToken = localStorage.getItem(AUTH_TOKEN);

    // Verificar se é uma requisição de autenticação/login
    const isAuthRequest = isAuthRoute(config.url);

    // Se o token já expirou e não estamos fazendo uma requisição de autenticação, cancelar a requisição
    if (tokenExpired && !isAuthRequest) {
      //console.log("[FetchInterceptor] Cancelando requisição devido a token expirado:",config.url);
      return Promise.reject(
        new Error("Token expirado, redirecionando para login")
      );
    }

    if (jwtToken) {
      config.headers[TOKEN_PAYLOAD_KEY] = `Bearer ${jwtToken}`;

      // Usar parâmetro de URL em vez de cabeçalhos para evitar cache
      if (!isAuthRequest) {
        const timestamp = new Date().getTime();
        const separator = config.url.includes("?") ? "&" : "?";
        config.url = `${config.url}${separator}_=${timestamp}`;
      }
    } else {
      // Se não estiver fazendo uma requisição pública ou de autenticação e não houver token, redirecionar para login
      if (!config.headers[PUBLIC_REQUEST_KEY] && !isAuthRequest) {
        redirectToLogin();
        throw new axios.Cancel("Requisição cancelada: token não encontrado");
      }
    }

    // Verificar informações do usuário no localStorage
    const userInfo = localStorage.getItem(AUTH_USER);
    if (userInfo && !isAuthRequest) {
      try {
        // Validar que é um JSON válido
        JSON.parse(userInfo);

        // Se userChanged for true, adicionar um cabeçalho especial para forçar o servidor a recarregar dados
        if (userChanged) {
          config.headers["X-User-Changed"] = "true";
        }
      } catch (e) {
        // Erro ao processar userInfo, não crítico
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
service.interceptors.response.use(
  (response) => {
    // Limpar a flag de token expirado em caso de resposta bem-sucedida
    tokenExpired = false;

    //console.log("[FetchInterceptor] URL da resposta interceptada:",response.config.url);

    // Verificar se a resposta contém dados do dashboard
    if (response.data && response.data.lastUpdate) {
      const lastUpdate = new Date(response.data.lastUpdate);
      if (isNaN(lastUpdate.getTime()) || lastUpdate.getTime() < 0) {
        response.data.lastUpdate = new Date().toISOString();
      }
    }

    if (response.config.url.includes("Indicadores")) {
      //console.log("[FetchInterceptor] Headers da resposta:",response.headers);
    }
    const xNewToken = response.headers["x-new-token"];
    //console.log("[FetchInterceptor] Novo token recebido:",xNewToken);

    if (typeof xNewToken !== "undefined") {
      localStorage.setItem(AUTH_TOKEN, xNewToken);
    }

    return response;
  },
  (error) => {
    // Capturar erros de rede
    if (!error.response) {
      notification.error({
        message: "Erro de Conexão",
        description:
          "Verifique sua conexão com a internet ou se o servidor está disponível.",
        duration: 4,
      });

      // Tratar erros de CORS especificamente
      if (
        (error.message && error.message.includes("NetworkError")) ||
        error.message.includes("Network Error") ||
        error.message.includes("CORS")
      ) {
        // Tentar carregar do cache se disponível
        window.dispatchEvent(
          new CustomEvent("network-error", {
            detail: { message: error.message, type: "cors" },
          })
        );
      }

      return Promise.reject(error);
    }

    const notificationParam = {
      message: "",
    };

    // Remove token and redirect
    if (
      error.response &&
      (error.response.status === 401 || error.response.status === 403)
    ) {
      console.log(
        "[FetchInterceptor] Erro de autenticação:",
        error.response.status
      );
      tokenExpired = true;

      notificationParam.message = "Falha na Autenticação";
      notificationParam.description = "Por favor, faça login novamente";

      // Verificar se já estamos na tela de login para evitar redirecionamento em loop
      const currentPath = window.location.pathname;
      const isAuthPath =
        currentPath.includes("/login") || currentPath.includes("/auth");

      if (!isAuthPath) {
        // Limpar dados de autenticação imediatamente
        clearAuthData(true);

        // Redirecionar para login com timestamp para evitar cache
        const timestamp = new Date().getTime();
        window.location.href = `${ENTRY_ROUTE}?_=${timestamp}`;
      }
    }

    if (error.response && error.response.status === 404) {
      // Extrair o endpoint da URL para exibir uma mensagem mais amigável
      const url = error.config?.url || "";

      // Silenciar logs e notificações para o endpoint de integrações dinâmicas do usuário
      if (url.includes("/api/IntegracaoDinamica/usuario/")) {
        // Não exibir notificação nem logar erro para esse caso
        return Promise.reject(error);
      }

      // Silenciar logs e notificações para o endpoint de primeiro login
      if (url.includes("/DashboardConfig/meu-perfil")) {
        // Não exibir notificação nem logar erro para esse caso
        return Promise.reject(error);
      }

      // Mensagem específica para endpoints comuns
      let descricao = `Não foi possível encontrar o recurso: ${url}`;

      if (
        url.includes("DashboardConfig/atualizar-visibilidade") ||
        url.includes("DashboardConfig/cards")
      ) {
        descricao =
          "Erro ao atualizar visibilidade dos cards. A funcionalidade pode estar temporariamente indisponível.";
      } else if (url.includes("DashboardConfig/layout")) {
        descricao =
          "Erro ao atualizar layout do dashboard. A funcionalidade pode estar temporariamente indisponível.";
      }

      notificationParam.message = "Recurso não encontrado";
      notificationParam.description = descricao;

      // Log detalhado para debug de erros 404
      console.error(
        `[FetchInterceptor] Recurso não encontrado (404): ${url}`,
        error.response
      );
    }

    if (error.response && error.response.status === 400) {
      notificationParam.message = "Requisição Inválida";
      notificationParam.description =
        error.response.data?.message || "Verifique os dados enviados";
    }

    if (error.response && error.response.status === 500) {
      // Não exibir notificação de erro 500 para o endpoint de primeiro acesso do dashboard
      const url = error.config?.url || "";
      if (url.includes("/DashboardConfig/meu-perfil")) {
        // Silenciar notificação para esse caso
        return Promise.reject(error);
      }
      notificationParam.message = "Erro Interno do Servidor";
      notificationParam.description =
        "Ocorreu um erro interno. Tente novamente mais tarde.";
    }

    if (error.response && error.response.status === 508) {
      notificationParam.message = "Tempo Limite Excedido";
      notificationParam.description =
        "O servidor demorou muito para responder. Tente novamente mais tarde.";
    }

    if (notificationParam.message) {
      notification.error(notificationParam);
    }

    return Promise.reject(error);
  }
);

// Adicionar ouvinte de evento para mudanças de usuário
window.addEventListener("storage", (event) => {
  if (
    event.key === AUTH_USER ||
    event.key === USER_ID ||
    event.key === AUTH_TOKEN
  ) {
    checkUserChange();
  }
});

export default service;
